import { useState, useContext, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import { useNavigate } from "react-router-dom";
import TablePagination from "../../../components/pagination/TablePagination";
import UsageChart from "./UsageChart";
import UsageFilters from "./UsageFilters";
import { useTenantRole } from "../../../components/TenantRoleDisplay";

interface ApiScope {
  api_scope_id: number;
  scope_key: string;
  scope_display_name: string;
  description?: string;
  is_active?: boolean;
  crm_entity_id?: number;
}

interface ApiWebhookEvent {
  api_webhook_event_id: number;
  api_webhook_event_key: string;
  api_webhook_event_display_name: string;
  description?: string;
  is_active?: boolean;
  crm_entity_id?: number;
}

interface ApiWebhookSubscription {
  api_webhook_subscription_id: number;
  webhook_url: string;
  status: string;
  webhook_event_mode: string;
  description: string;
  created_datetime: string;
  updated_datetime: string;
  webhook_failure_url?: string;
  webhook_success_url?: string;
  api_client_id: number;
  created_by: number;
  updated_by: number;
  webhook_event?: ApiWebhookEvent[];
}

interface ApiWebhookDeliveryAuditLog {
  api_webhook_delivery_audit_log_id: number;
  delivery_status: string;
  attempt_count: number;
  last_attempt_datetime: string;
  response_http_code: number | null;
  response_time_ms: number;
  response: string;
  created_datetime: string;
  payload: string;
  api_webhook_subscription: {
    webhook_url: string;
    webhook_event_mode: string;
  };
  api_webhook_event: {
    api_webhook_event_display_name: string;
    description: string;
  };
  api_client: {
    client_name: string;
    api_client_id: number;
  };
  tenant_id: number;
}

interface TenantSubscription {
  subscription_end_date: string;
}

interface ApiClient {
  api_client_id: number;
  client_name: string;
  client_id: string;
  api_credential_id: number;
  description: string;
  api_key: string;
  status: string;
  client_logo: string | null;
  created_datetime: string;
  tenant_id: number;
  expire_datetime: string;
  scopes?: ApiScope[] | null;
}

const route = all_routes;
const ApiIntegrationSettings = () => {
  const { selectedTenantId, auth } = useContext(AuthContext);
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();

  const userRole = useTenantRole(selectedTenantId || 0);
  const isApiAdmin =
    userRole?.role_key?.toLowerCase() === "apiadmin" ||
    userRole?.role_name?.toLowerCase() === "apiadmin" ||
    userRole?.role_key?.toLowerCase() === "api_admin" ||
    userRole?.role_name?.toLowerCase() === "api_admin";

  const validateExpiryDate = async (expiryDate: string): Promise<boolean> => {
    if (isApiAdmin) return true;
    if (!selectedTenantId) return false;

    if (!expiryDate) return true;

    try {
      const response = await fetch(
        `${API_BASE_URL}/tenant-subscription/?tenant_id=${selectedTenantId}&is_active=true`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const subscriptions: TenantSubscription[] = data.results || [];

        if (subscriptions.length === 0) {

          return true;
        }

        let maxEndDate: Date | null = null;

        subscriptions.forEach(sub => {
          if (sub.subscription_end_date) {
            const subDate = new Date(sub.subscription_end_date);
            if (!maxEndDate || subDate > maxEndDate) {
              maxEndDate = subDate;
            }
          }
        });

        if (maxEndDate) {
          const limitDate = new Date(maxEndDate);
          limitDate.setHours(23, 59, 59, 999);

          const selectedDate = new Date(expiryDate);
          if (selectedDate > limitDate) {
            MySwal.fire({
              title: "Warning",
              text: "Please choose API credential's expiration date within your tenant subscription expiry date.",
              icon: "warning",
              confirmButtonText: "OK",
            });
            return false;
          }
        }

        return true;
      }
    } catch (error) {
      console.error("Error fetching tenant subscriptions:", error);

    }
    return true;
  };


  const [activeTab, setActiveTab] = useState<"api-clients" | "usage">(
    "api-clients"
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ApiClient | null>(null);
  const [apiClients, setApiClients] = useState<ApiClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [detailView, setDetailView] = useState(false);
  const [detailTab, setDetailTab] = useState<"general" | "webhook">("general");
  const [usageTab, setUsageTab] = useState<"api-usage" | "webhook-usage">(
    "api-usage"
  );
  const [availableScopes, setAvailableScopes] = useState<ApiScope[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<number[]>([]);
  const [selectedScopesData, setSelectedScopesData] = useState<ApiScope[]>([]);
  const [showScopeDropdown, setShowScopeDropdown] = useState(false);
  const [scopesPage, setScopesPage] = useState(1);
  const [scopesHasMore, setScopesHasMore] = useState(true);
  const [isLoadingMoreScopes, setIsLoadingMoreScopes] = useState(false);
  const [scopeSearchQuery, setScopeSearchQuery] = useState("");
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsPage, setAuditLogsPage] = useState(1);
  const [auditLogsTotalCount, setAuditLogsTotalCount] = useState(0);
  const [auditLogsStats, setAuditLogsStats] = useState({
    total_requests: 0,
    successful: 0,
    failed: 0,
  });

  const [webhookDeliveryLogs, setWebhookDeliveryLogs] = useState<
    ApiWebhookDeliveryAuditLog[]
  >([]);
  const [webhookDeliveryLogsLoading, setWebhookDeliveryLogsLoading] =
    useState(false);
  const [webhookDeliveryLogsPage, setWebhookDeliveryLogsPage] = useState(1);
  const [webhookDeliveryLogsTotalCount, setWebhookDeliveryLogsTotalCount] =
    useState(0);
  const [webhookDeliveryLogsStats, setWebhookDeliveryLogsStats] = useState({
    total_requests: 0,
    successful: 0,
    failed: 0,
  });

  const [webhookSubscriptions, setWebhookSubscriptions] = useState<
    ApiWebhookSubscription[]
  >([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [isEditingWebhook, setIsEditingWebhook] = useState(false);
  const [selectedWebhook, setSelectedWebhook] =
    useState<ApiWebhookSubscription | null>(null);
  const [availableWebhookEvents, setAvailableWebhookEvents] = useState<
    ApiWebhookEvent[]
  >([]);
  const [selectedWebhookEvents, setSelectedWebhookEvents] = useState<number[]>(
    []
  );
  const [selectedWebhookEventsData, setSelectedWebhookEventsData] = useState<
    ApiWebhookEvent[]
  >([]);
  const [showWebhookEventDropdown, setShowWebhookEventDropdown] =
    useState(false);
  const [webhookEventSearchQuery, setWebhookEventSearchQuery] = useState("");
  const [webhookEventsPage, setWebhookEventsPage] = useState(1);
  const [webhookEventsHasMore, setWebhookEventsHasMore] = useState(true);
  const [isLoadingMoreWebhookEvents, setIsLoadingMoreWebhookEvents] =
    useState(false);

  const [webhookFormData, setWebhookFormData] = useState({
    webhook_url: "",
    webhook_success_url: "",
    webhook_failure_url: "",
    status: "active",
    webhook_event_mode: "custom" as "custom" | "global",
    description: "",
  });

  const [urlValidationStatus, setUrlValidationStatus] = useState<
    "idle" | "validating" | "valid" | "invalid"
  >("idle");
  const [urlValidationData, setUrlValidationData] = useState<any>(null);
  const urlValidationTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [formData, setFormData] = useState({
    client_name: "",
    description: "",
    client_logo: null as File | null,
    expire_datetime: "",
    scope_mode: "custom" as "custom" | "global",
  });

  const [editFormData, setEditFormData] = useState({
    client_name: "",
    description: "",
    status: "active",
    client_logo: null as File | null,
    existing_logo: null as string | null,
    expire_datetime: "",
    scope_mode: "custom" as "custom" | "global",
  });

  const [apiUsageStartDate, setApiUsageStartDate] = useState<Date | null>(null);
  const [apiUsageEndDate, setApiUsageEndDate] = useState<Date | null>(null);
  const [apiUsageClientId, setApiUsageClientId] = useState<number | null>(null);

  const [webhookUsageStartDate, setWebhookUsageStartDate] =
    useState<Date | null>(null);
  const [webhookUsageEndDate, setWebhookUsageEndDate] = useState<Date | null>(
    null
  );
  const [webhookUsageClientId, setWebhookUsageClientId] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (activeTab === "api-clients" && (isApiAdmin || selectedTenantId)) {
      fetchApiClients();
    }
  }, [activeTab, selectedTenantId, isApiAdmin]);

  useEffect(() => {
    if (activeTab === "usage" && (isApiAdmin || selectedTenantId)) {
      fetchAuditLogs(
        1,
        12,
        apiUsageStartDate,
        apiUsageEndDate,
        apiUsageClientId
      );
      setAuditLogsPage(1);
    }
  }, [
    activeTab,
    selectedTenantId,
    apiUsageStartDate,
    apiUsageEndDate,
    apiUsageClientId,
    isApiAdmin,
  ]);

  useEffect(() => {
    if (
      activeTab === "usage" &&
      usageTab === "webhook-usage" &&
      (isApiAdmin || selectedTenantId)
    ) {
      fetchWebhookDeliveryLogs(
        1,
        12,
        webhookUsageStartDate,
        webhookUsageEndDate,
        webhookUsageClientId
      );
      setWebhookDeliveryLogsPage(1);
    }
  }, [
    activeTab,
    usageTab,
    selectedTenantId,
    webhookUsageStartDate,
    webhookUsageEndDate,
    webhookUsageClientId,
    isApiAdmin,
  ]);

  useEffect(() => {
    fetchApiScopes();
    fetchWebhookEvents();
  }, []);

  useEffect(() => {
    if (detailTab === "webhook" && selectedClient) {
      fetchWebhookSubscriptions(selectedClient.api_client_id);
    }
  }, [detailTab, selectedClient]);

  const fetchApiScopes = async (
    page: number = 1,
    scope_display_name: string = "",
    isLoadMore: boolean = false
  ) => {
    try {
      if (isLoadMore) {
        setIsLoadingMoreScopes(true);
      }

      let url = `${API_BASE_URL}/api-scope/?page=${page}&page_size=50`;
      if (scope_display_name.trim()) {
        url += `&scope_display_name=${encodeURIComponent(
          scope_display_name.trim()
        )}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const scopes = data.results || [];
        const hasMore = data.next !== null;

        if (isLoadMore) {
          setAvailableScopes((prev) => [...prev, ...scopes]);
        } else {
          setAvailableScopes(scopes);
        }

        setScopesHasMore(hasMore);
      }
    } catch (error) {
      console.error("Error fetching API scopes:", error);
    } finally {
      if (isLoadMore) {
        setIsLoadingMoreScopes(false);
      }
    }
  };

  const fetchApiClients = async () => {
    if (!isApiAdmin && !selectedTenantId) return;

    setLoading(true);
    try {
      const url = isApiAdmin 
        ? `${API_BASE_URL}/api-client/?created_by=${auth?.user_id}`
        : `${API_BASE_URL}/api-client/?tenant_id=${selectedTenantId}`;
      const response = await fetch(
        url,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setApiClients(data.results || []);
      }
    } catch (error) {
      console.error("Error fetching API clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async (
    page: number = 1,
    pageSize: number = 12,
    startDate: Date | null = null,
    endDate: Date | null = null,
    clientId: number | null = null
  ) => {
    if (!isApiAdmin && !selectedTenantId) return;

    setAuditLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (!isApiAdmin && selectedTenantId) {
        params.append("tenant_id", selectedTenantId.toString());
      }
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());

      if (startDate)
        params.append(
          "request_datetime_gte",
          startDate.toISOString().split("T")[0]
        );
      if (endDate)
        params.append(
          "request_datetime_lte",
          endDate.toISOString().split("T")[0]
        );
      if (clientId) params.append("client_id", clientId.toString());

      const response = await fetch(
        `${API_BASE_URL}/api-audit-log/?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.results || []);
        setAuditLogsTotalCount(data.count || 0);
        setAuditLogsPage(page);

        if (data.stats) {
          setAuditLogsStats({
            total_requests: data.stats.total_requests || 0,
            successful: data.stats.successful || 0,
            failed: data.stats.failed || 0,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    } finally {
      setAuditLogsLoading(false);
    }
  };

  const fetchWebhookDeliveryLogs = async (
    page: number = 1,
    pageSize: number = 12,
    startDate: Date | null = null,
    endDate: Date | null = null,
    clientId: number | null = null
  ) => {
    if (!isApiAdmin && !selectedTenantId) return;

    setWebhookDeliveryLogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());

      if (startDate)
        params.append(
          "created_datetime_gte",
          startDate.toISOString().split("T")[0]
        );
      if (endDate)
        params.append(
          "created_datetime_lte",
          endDate.toISOString().split("T")[0]
        );
      if (clientId) params.append("api_client_id", clientId.toString());

      const response = await fetch(
        `${API_BASE_URL}/api-webhook-delivery-audit-log/?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setWebhookDeliveryLogs(data.results || []);
        setWebhookDeliveryLogsTotalCount(data.count || 0);
        setWebhookDeliveryLogsPage(page);

        if (data.stats) {
          setWebhookDeliveryLogsStats({
            total_requests: data.stats.total_requests || 0,
            successful: data.stats.successful || 0,
            failed: data.stats.failed || 0,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching webhook delivery logs:", error);
    } finally {
      setWebhookDeliveryLogsLoading(false);
    }
  };

  const extractHttpMethodFromPayload = (payload: string): string => {
    try {
      const parsed = JSON.parse(payload);
      return parsed?.event?.http_method || "N/A";
    } catch {
      return "N/A";
    }
  };

  const fetchWebhookSubscriptions = async (apiClientId: number) => {
    if (!apiClientId) return;

    setWebhookLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api-webhook-subscripiton/?api_client_id=${apiClientId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setWebhookSubscriptions(data.results || []);
      }
    } catch (error) {
      console.error("Error fetching webhook subscriptions:", error);
    } finally {
      setWebhookLoading(false);
    }
  };

  const fetchWebhookEvents = async (
    page: number = 1,
    api_webhook_event_display_name: string = "",
    isLoadMore: boolean = false
  ) => {
    try {
      if (isLoadMore) {
        setIsLoadingMoreWebhookEvents(true);
      }

      let url = `${API_BASE_URL}/api-webhook-event/?page=${page}&page_size=50`;
      if (api_webhook_event_display_name.trim()) {
        url += `&api_webhook_event_display_name=${encodeURIComponent(
          api_webhook_event_display_name.trim()
        )}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const events = data.results || [];
        const hasMore = data.next !== null;

        if (isLoadMore) {
          setAvailableWebhookEvents((prev) => [...prev, ...events]);
        } else {
          setAvailableWebhookEvents(events);
        }

        setWebhookEventsHasMore(hasMore);
      }
    } catch (error) {
      console.error("Error fetching webhook events:", error);
    } finally {
      if (isLoadMore) {
        setIsLoadingMoreWebhookEvents(false);
      }
    }
  };

  const handleWebhookInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setWebhookFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "webhook_url") {
      if (urlValidationTimerRef.current) {
        clearTimeout(urlValidationTimerRef.current);
      }

      urlValidationTimerRef.current = setTimeout(() => {
        validateWebhookUrl(value);
      }, 1000);
    }
  };

  const toggleWebhookEvent = (eventId: number) => {
    const event = availableWebhookEvents.find(
      (e) => e.api_webhook_event_id === eventId
    );
    setSelectedWebhookEvents((prev) => {
      if (prev.includes(eventId)) {
        return prev.filter((id) => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
    setSelectedWebhookEventsData((prev) => {
      if (prev.some((e) => e.api_webhook_event_id === eventId)) {
        return prev.filter((e) => e.api_webhook_event_id !== eventId);
      } else if (event) {
        return [...prev, event];
      }
      return prev;
    });
  };

  const removeWebhookEvent = (eventId: number) => {
    setSelectedWebhookEvents((prev) => prev.filter((id) => id !== eventId));
    setSelectedWebhookEventsData((prev) =>
      prev.filter((e) => e.api_webhook_event_id !== eventId)
    );
  };

  const loadMoreWebhookEvents = async () => {
    if (!webhookEventsHasMore || isLoadingMoreWebhookEvents) {
      return;
    }

    const nextPage = webhookEventsPage + 1;
    setWebhookEventsPage(nextPage);
    await fetchWebhookEvents(nextPage, webhookEventSearchQuery, true);
  };

  const handleWebhookEventSearch = (query: string) => {
    setWebhookEventSearchQuery(query);
    setWebhookEventsPage(1);
    setWebhookEventsHasMore(true);
    fetchWebhookEvents(1, query, false);
  };

  const resetWebhookEventDropdownState = () => {
    setShowWebhookEventDropdown(false);
    setWebhookEventSearchQuery("");
    setWebhookEventsPage(1);
    setWebhookEventsHasMore(true);
  };

  const resetWebhookForm = () => {
    setWebhookFormData({
      webhook_url: "",
      webhook_success_url: "",
      webhook_failure_url: "",
      status: "active",
      webhook_event_mode: "custom",
      description: "",
    });
    setSelectedWebhookEvents([]);
    setSelectedWebhookEventsData([]);
    setWebhookEventSearchQuery("");
    setWebhookEventsPage(1);
    setWebhookEventsHasMore(true);
    setUrlValidationStatus("idle");
    setUrlValidationData(null);
    if (urlValidationTimerRef.current) {
      clearTimeout(urlValidationTimerRef.current);
    }
  };

  const validateWebhookUrl = async (webhook_url: string) => {
    if (!webhook_url.trim()) {
      setUrlValidationStatus("idle");
      setUrlValidationData(null);
      return;
    }

    setUrlValidationStatus("validating");

    try {
      const response = await fetch(
        `${API_BASE_URL}/api-webhook-validate-url/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
          body: JSON.stringify({ webhook_url }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUrlValidationData(data);

        if (data.is_reachable) {
          setUrlValidationStatus("valid");
        } else {
          setUrlValidationStatus("invalid");
        }
      } else {
        setUrlValidationStatus("invalid");
        setUrlValidationData(null);
      }
    } catch (error) {
      console.error("Error validating webhook URL:", error);
      setUrlValidationStatus("invalid");
      setUrlValidationData(null);
    }
  };

  const handleCreateWebhook = async () => {
    if (!selectedClient) return;

    if (!webhookFormData.webhook_url.trim()) {
      MySwal.fire({
        title: "Error",
        text: "Webhook URL is required",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setWebhookLoading(true);

    try {
      const payload: any = {
        webhook_url: webhookFormData.webhook_url,
        status: webhookFormData.status,
        webhook_event_mode: webhookFormData.webhook_event_mode,
        description: webhookFormData.description,
        api_client_id: selectedClient.api_client_id,
        created_by: auth?.user_id,
        updated_by: auth?.user_id,
      };

      if (!isApiAdmin && selectedTenantId) {
        payload.tenant_id = selectedTenantId;
      }

      if (webhookFormData.webhook_success_url) {
        payload.webhook_success_url = webhookFormData.webhook_success_url;
      }

      if (webhookFormData.webhook_failure_url) {
        payload.webhook_failure_url = webhookFormData.webhook_failure_url;
      }

      if (webhookFormData.webhook_event_mode === "custom") {
        payload.webhook_event = selectedWebhookEvents;
      }

      const response = await fetch(
        `${API_BASE_URL}/api-webhook-subscripiton/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create webhook subscription");
      }

      MySwal.fire({
        title: "Success!",
        text: "Webhook subscription created successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      setShowWebhookModal(false);
      resetWebhookForm();
      await fetchWebhookSubscriptions(selectedClient.api_client_id);
    } catch (error) {
      console.error("Error creating webhook subscription:", error);
      MySwal.fire({
        title: "Error",
        text: "Failed to create webhook subscription. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleUpdateWebhook = async () => {
    if (!selectedClient || !selectedWebhook) return;

    if (!webhookFormData.webhook_url.trim()) {
      MySwal.fire({
        title: "Error",
        text: "Webhook URL is required",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setWebhookLoading(true);

    try {
      const payload: any = {
        webhook_url: webhookFormData.webhook_url,
        status: webhookFormData.status,
        webhook_event_mode: webhookFormData.webhook_event_mode,
        description: webhookFormData.description,
        updated_by: auth?.user_id,
      };

      if (!isApiAdmin && selectedTenantId) {
        payload.tenant_id = selectedTenantId;
      }

      if (webhookFormData.webhook_success_url) {
        payload.webhook_success_url = webhookFormData.webhook_success_url;
      }

      if (webhookFormData.webhook_failure_url) {
        payload.webhook_failure_url = webhookFormData.webhook_failure_url;
      }

      if (webhookFormData.webhook_event_mode === "custom") {
        payload.webhook_event = selectedWebhookEvents;
      }

      const response = await fetch(
        `${API_BASE_URL}/api-webhook-subscripiton/${selectedWebhook.api_webhook_subscription_id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update webhook subscription");
      }

      MySwal.fire({
        title: "Success!",
        text: "Webhook subscription updated successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      setShowWebhookModal(false);
      setIsEditingWebhook(false);
      setSelectedWebhook(null);
      resetWebhookForm();
      await fetchWebhookSubscriptions(selectedClient.api_client_id);
    } catch (error) {
      console.error("Error updating webhook subscription:", error);
      MySwal.fire({
        title: "Error",
        text: "Failed to update webhook subscription. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleDeleteWebhook = async (webhook: ApiWebhookSubscription) => {
    const result = await MySwal.fire({
      title: "Delete Webhook?",
      html: `
        <div class="text-center">
          <p>Are you sure you want to delete this webhook subscription?</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      width: "500px",
    });

    if (!result.isConfirmed) return;

    setWebhookLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api-webhook-subscripiton/${webhook.api_webhook_subscription_id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete webhook subscription");
      }

      MySwal.fire({
        title: "Deleted!",
        text: "Webhook subscription has been deleted successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });

      if (selectedClient) {
        await fetchWebhookSubscriptions(selectedClient.api_client_id);
      }
    } catch (error) {
      console.error("Error deleting webhook subscription:", error);
      MySwal.fire({
        title: "Error",
        text: "Failed to delete webhook subscription. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleEditWebhook = (webhook: ApiWebhookSubscription) => {
    setSelectedWebhook(webhook);
    setIsEditingWebhook(true);
    setWebhookFormData({
      webhook_url: webhook.webhook_url,
      webhook_success_url: webhook.webhook_success_url || "",
      webhook_failure_url: webhook.webhook_failure_url || "",
      status: webhook.status,
      webhook_event_mode: webhook.webhook_event_mode as "custom" | "global",
      description: webhook.description,
    });
    setSelectedWebhookEvents(
      webhook.webhook_event?.map((e) => e.api_webhook_event_id) || []
    );
    setSelectedWebhookEventsData(webhook.webhook_event || []);
    setShowWebhookModal(true);

    if (webhook.webhook_url) {
      validateWebhookUrl(webhook.webhook_url);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({ ...prev, client_logo: e.target.files![0] }));
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: "",
      description: "",
      client_logo: null,
      expire_datetime: "",
      scope_mode: "custom",
    });
    setSelectedScopes([]);
    setSelectedScopesData([]);
    setScopeSearchQuery("");
    setScopesPage(1);
    setScopesHasMore(true);
  };

  const toggleScope = (scopeId: number) => {
    const scope = availableScopes.find((s) => s.api_scope_id === scopeId);
    setSelectedScopes((prev) => {
      if (prev.includes(scopeId)) {
        return prev.filter((id) => id !== scopeId);
      } else {
        return [...prev, scopeId];
      }
    });
    setSelectedScopesData((prev) => {
      if (prev.some((s) => s.api_scope_id === scopeId)) {
        return prev.filter((s) => s.api_scope_id !== scopeId);
      } else if (scope) {
        return [...prev, scope];
      }
      return prev;
    });
  };

  const removeScope = (scopeId: number) => {
    setSelectedScopes((prev) => prev.filter((id) => id !== scopeId));
    setSelectedScopesData((prev) => prev.filter((s) => s.api_scope_id !== scopeId));
  };

  const loadMoreScopes = async () => {
    if (!scopesHasMore || isLoadingMoreScopes) {
      return;
    }

    const nextPage = scopesPage + 1;
    setScopesPage(nextPage);
    await fetchApiScopes(nextPage, scopeSearchQuery, true);
  };

  const handleScopeSearch = (query: string) => {
    setScopeSearchQuery(query);
    setScopesPage(1);
    setScopesHasMore(true);
    fetchApiScopes(1, query, false);
  };

  const resetScopeDropdownState = () => {
    setShowScopeDropdown(false);
    setScopeSearchQuery("");
    setScopesPage(1);
    setScopesHasMore(true);
  };

  const getAuditStats = () => {
    return {
      total: auditLogsStats.total_requests,
      successful: auditLogsStats.successful,
      failed: auditLogsStats.failed,
    };
  };

  const handleClientNameClick = async (clientId: number) => {
    const client = apiClients.find((c) => c.api_client_id === clientId);
    if (client) {
      handleViewDetails(client);
      setActiveTab("api-clients");
    } else {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api-client/${clientId}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${localStorage.getItem("authCred")}`,
            },
          }
        );
        if (response.ok) {
          const clientData = await response.json();
          handleViewDetails(clientData);
          setActiveTab("api-clients");
        }
      } catch (error) {
        console.error("Error fetching client details:", error);
      }
    }
  };

  const handleAuditLogsPageChange = (page: number) => {
    setAuditLogsPage(page);
    fetchAuditLogs(
      page,
      12,
      apiUsageStartDate,
      apiUsageEndDate,
      apiUsageClientId
    );
  };

  const handleWebhookDeliveryLogsPageChange = (page: number) => {
    setWebhookDeliveryLogsPage(page);
    fetchWebhookDeliveryLogs(
      page,
      12,
      webhookUsageStartDate,
      webhookUsageEndDate,
      webhookUsageClientId
    );
  };

  const getWebhookDeliveryStats = () => {
    return {
      total: webhookDeliveryLogsStats.total_requests,
      successful: webhookDeliveryLogsStats.successful,
      failed: webhookDeliveryLogsStats.failed,
    };
  };

  const showApiSecretModal = (
    apiKey: string,
    apiSecret: string,
    clientName: string,
    downloadUrl?: string
  ) => {
    MySwal.fire({
      title: "API Credentials Created!",
      html: `
        <div class="text-start">
          <div class="alert alert-primary mb-3">
            <i class="ti ti-alert-circle me-2"></i>
            <strong>Important:</strong> The API Secret will only be shown once. Please copy it now! The API Key will remain accessible.
          </div>
          <div class="mb-3">
            <label class="form-label fw-bold">API Key:</label>
            <div class="input-group">
              <input type="text" class="form-control" id="api-key-input" value="${apiKey}" readonly />
              <button class="btn btn-outline-primary" type="button" onclick="navigator.clipboard.writeText('${apiKey}')">
                <i class="ti ti-copy"></i>
              </button>
            </div>
          </div>
          <div class="mb-3">
            <label class="form-label fw-bold">API Secret:</label>
            <div class="input-group">
              <input type="text" class="form-control" id="api-secret-input" value="${apiSecret}" readonly />
              <button class="btn btn-outline-primary" type="button" onclick="navigator.clipboard.writeText('${apiSecret}')">
                <i class="ti ti-copy"></i>
              </button>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: downloadUrl ? "Download JSON" : "Download JSON",
      cancelButtonText: "Close",
      width: "600px",
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (downloadUrl) {
          try {
            const response = await fetch(downloadUrl);
            const jsonData = await response.json();

            const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
              type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `api-credentials-${clientName
              .replace(/\s+/g, "-")
              .toLowerCase()}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } catch (error) {
            console.error("Error downloading credentials:", error);
            MySwal.fire({
              title: "Error",
              text: "Failed to download credentials. Please try again.",
              icon: "error",
              confirmButtonText: "OK",
            });
          }
        } else {
          const credentials = {
            client_name: clientName,
            api_key: apiKey,
            api_secret: apiSecret,
            created_at: new Date().toISOString(),
          };

          const blob = new Blob([JSON.stringify(credentials, null, 2)], {
            type: "application/json",
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `api-credentials-${clientName
            .replace(/\s+/g, "-")
            .toLowerCase()}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      }
    });
  };

  const handleRegenerateSecret = async (apiKey: string, clientName: string) => {
    const result = await MySwal.fire({
      title: "Regenerate API Secret?",
      html: `
        <div class="text-start">
          <div class="alert alert-primary mb-3">
            <strong>Warning:</strong> This will invalidate the current API secret. Any applications using the old secret will stop working.
          </div>
          <p>Are you sure you want to regenerate the API secret for <strong>${clientName}</strong>?</p>
        </div>
      `,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Yes, Regenerate",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#6b2ee7",
      width: "500px",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/re-generate-secret/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
        body: JSON.stringify({ api_key: apiKey }),
      });

      if (!response.ok) {
        throw new Error("Failed to regenerate API secret");
      }

      const data = await response.json();

      if (data.success) {
        showApiSecretModal(
          apiKey,
          data.api_secret,
          clientName,
          data.download_url
        );
      }
    } catch (error) {
      console.error("Error regenerating API secret:", error);
      MySwal.fire({
        title: "Error",
        text: "Failed to regenerate API secret. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (client: ApiClient) => {
    setSelectedClient(client);
    setDetailView(true);
    setDetailTab("general");
    setShowApiKey(false);
  };

  const handleDeleteApiClient = async (client: ApiClient) => {
    const result = await MySwal.fire({
      title: "Delete API Client?",
      html: `
        <div class="text-center">
          <p>Are you sure you want to delete <strong>${client.client_name}</strong>?</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      width: "500px",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api-client/${client.api_client_id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete API client");
      }

      MySwal.fire({
        title: "Deleted!",
        text: "API client has been deleted successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });

      await fetchApiClients();
    } catch (error) {
      console.error("Error deleting API client:", error);
      MySwal.fire({
        title: "Error",
        text: "Failed to delete API client. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (client: ApiClient) => {
    setSelectedClient(client);
    const scopeIds = client.scopes
      ? client.scopes.map((scope) => scope.api_scope_id)
      : [];
    setEditFormData({
      client_name: client.client_name,
      description: client.description,
      status: client.status,
      client_logo: null,
      existing_logo: client.client_logo,
      expire_datetime: client.expire_datetime,
      scope_mode: scopeIds.length > 0 ? "custom" : "global",
    });
    setSelectedScopes(scopeIds);
    setSelectedScopesData(client.scopes || []);
    setShowEditModal(true);
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setEditFormData((prev) => ({ ...prev, client_logo: e.target.files![0] }));
    }
  };

  const handleUpdateApiClient = async () => {
    if (!selectedClient) return;

    if (!editFormData.client_name.trim()) {
      MySwal.fire({
        title: "Error",
        text: "Client name is required",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    if (isApiAdmin && !editFormData.expire_datetime) {
      MySwal.fire({
        title: "Error",
        text: "Expiration Date is required",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    if (editFormData.expire_datetime) {
      const isValid = await validateExpiryDate(editFormData.expire_datetime);
      if (!isValid) return;
    }

    setLoading(true);

    try {
      let response;

      if (editFormData.client_logo) {
        const formDataPayload = new FormData();
        formDataPayload.append("client_name", editFormData.client_name);
        formDataPayload.append("description", editFormData.description);
        formDataPayload.append("status", editFormData.status);
        if (!isApiAdmin && selectedTenantId) {
          formDataPayload.append("tenant_id", selectedTenantId.toString());
        }
        formDataPayload.append("updated_by", auth?.user_id?.toString() || "");
        formDataPayload.append("client_logo", editFormData.client_logo);

        response = await fetch(
          `${API_BASE_URL}/api-client/${selectedClient.api_client_id}/`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Basic ${localStorage.getItem("authCred")}`,
            },
            body: formDataPayload,
          }
        );
      } else {
        const payload: any = {
          client_name: editFormData.client_name,
          description: editFormData.description,
          status: editFormData.status,
          updated_by: auth?.user_id,
        };

        if (!isApiAdmin && selectedTenantId) {
          payload.tenant_id = selectedTenantId;
        }

        response = await fetch(
          `${API_BASE_URL}/api-client/${selectedClient.api_client_id}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${localStorage.getItem("authCred")}`,
            },
            body: JSON.stringify(payload),
          }
        );
      }

      if (!response.ok) {
        throw new Error("Failed to update API client");
      }

      const data = await response.json();

      const credentialPayload: any = {
        scopes: editFormData.scope_mode === "custom" ? selectedScopes : [],
        scope_mode: editFormData.scope_mode,
      };

      if (editFormData.expire_datetime) {
        credentialPayload.expire_datetime = editFormData.expire_datetime;
      }

      const credentialResponse = await fetch(
        `${API_BASE_URL}/api-credential/${selectedClient.api_credential_id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
          body: JSON.stringify(credentialPayload),
        }
      );

      if (!credentialResponse.ok) {
        throw new Error("Failed to update API credentials");
      }

      MySwal.fire({
        title: "Success!",
        text: "API client updated successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      setShowEditModal(false);
      await fetchApiClients();

      if (detailView) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}/api-client/${selectedClient.api_client_id}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${localStorage.getItem("authCred")}`,
            },
          }
        );

        if (refreshResponse.ok) {
          const refreshedData = await refreshResponse.json();
          setSelectedClient(refreshedData);
        }
      } else {
        setSelectedClient(null);
      }
    } catch (error) {
      console.error("Error updating API client:", error);
      MySwal.fire({
        title: "Error",
        text: "Failed to update API client. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiClient = async () => {
    if (!formData.client_name.trim()) {
      MySwal.fire({
        title: "Error",
        text: "Client name is required",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    if (isApiAdmin && !formData.expire_datetime) {
      MySwal.fire({
        title: "Error",
        text: "Expiration Date is required",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    if (formData.expire_datetime) {
      const isValid = await validateExpiryDate(formData.expire_datetime);
      if (!isValid) return;
    }

    setLoading(true);

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("client_name", formData.client_name);
      formDataPayload.append("description", formData.description);
      if (!isApiAdmin && selectedTenantId) {
        formDataPayload.append("tenant_id", selectedTenantId.toString());
      }
      formDataPayload.append("created_by", auth?.user_id?.toString() || "");
      formDataPayload.append("updated_by", auth?.user_id?.toString() || "");
      formDataPayload.append("access_level", isApiAdmin ? "global" : "tenant");

      if (formData.client_logo) {
        formDataPayload.append("client_logo", formData.client_logo);
      }

      const clientResponse = await fetch(`${API_BASE_URL}/api-client/`, {
        method: "POST",
        headers: {
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
        body: formDataPayload,
      });

      if (!clientResponse.ok) {
        throw new Error("Failed to create API client");
      }

      const clientData = await clientResponse.json();

      const credentialPayload: any = {
        api_client_id: clientData.api_client_id,
        scopes: formData.scope_mode === "custom" ? selectedScopes : [],
        scope_mode: formData.scope_mode,
      };

      if (!isApiAdmin && selectedTenantId) {
        credentialPayload.tenant_id = selectedTenantId;
      }

      if (formData.expire_datetime) {
        credentialPayload.expire_datetime = formData.expire_datetime;
      }

      const credentialResponse = await fetch(
        `${API_BASE_URL}/api-credential/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
          body: JSON.stringify(credentialPayload),
        }
      );

      if (!credentialResponse.ok) {
        throw new Error("Failed to create API credentials");
      }

      const credentialData = await credentialResponse.json();

      setShowCreateModal(false);
      resetForm();

      showApiSecretModal(
        credentialData.api_key,
        credentialData.api_secret,
        formData.client_name
      );

      await fetchApiClients();
    } catch (error) {
      console.error("Error creating API client:", error);
      MySwal.fire({
        title: "Error",
        text: "Failed to create API client. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <style>
        {`
          .api-table-wrapper {
            overflow-x: auto;
            overflow-y: visible;
            -webkit-overflow-scrolling: touch;
            position: relative;
          }

          .api-table {
            min-width: 100%;
            width: 100%;
            table-layout: auto;
            position: relative;
          }

          .api-table th {
            background-color: #f8f9fa !important;
            border-bottom: none !important;
            font-weight: 600;
            color: #495057;
            padding: 12px 16px;
            vertical-align: middle;
            white-space: nowrap;
          }

          .api-table td {
            vertical-align: middle;
            padding: 12px 16px;
          }

          .api-table tbody tr {
            position: relative;
          }

          .api-table tbody tr td:last-child {
            position: relative;
            vertical-align: middle;
          }

          .api-client-name {
            min-width: 150px;
            max-width: 250px;
          }

          .api-client-name .text-primary:hover {
            text-decoration: underline;
            font-weight: 500;
          }

          .btn-outline-light:hover {
            background-color: #f8f9fa !important;
            border-color: #e0e0e0 !important;
          }

          .rounded-pill {
            border-radius: 50rem !important;
          }

          .transition-all {
            transition: all 0.2s ease-in-out;
          }

          .api-client-id {
            min-width: 150px;
            max-width: 200px;
            word-break: break-all;
          }

          .api-created-date {
            min-width: 180px;
          }

          .api-status {
            min-width: 100px;
            text-align: center;
          }

          .api-actions {
            min-width: 80px;
            text-align: center;
          }

          .api-table .table-action {
            position: static !important;
          }

          .api-table .table-action .dropdown-menu {
            z-index: 1050 !important;
            min-width: 150px;
            border: 1px solid #e0e0e0;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            border-radius: 6px;
            display: none;
            background-color: white !important;
            white-space: nowrap;
          }

          .api-table .table-action .dropdown-menu.show {
            display: block !important;
          }

          .api-table .table-action .action-icon {
            position: relative;
            z-index: 1;
          }

          .api-table .table-action .dropdown-item {
            text-align: left;
            border: none;
            background: none;
            width: 100%;
            padding: 8px 16px;
            font-size: 14px;
            color: #495057;
            cursor: pointer;
            transition: none;
          }

          .api-table .table-action .dropdown-item:hover {
            background-color: #f8f9fa;
          }

          .api-table .table-action .dropdown-item i {
            margin-right: 8px;
            width: 16px;
            text-align: center;
          }

          .api-table .table-action .dropdown-item:focus {
            box-shadow: none !important;
            outline: none !important;
          }

          .api-table .table-action .action-icon {
            background: none !important;
            border: none !important;
            box-shadow: none !important;
            transition: none;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 4px;
          }

          .api-table .table-action .action-icon:hover {
            background-color: #e9ecef !important;
            transform: scale(1.05);
            transition: all 0.2s ease;
          }

          .card-body {
            overflow: visible !important;
          }

          @media (min-width: 992px) {
            .table-responsive.api-table-wrapper {
              overflow: visible !important;
            }

            .api-detail-modal,
            .api-edit-modal,
            .api-create-modal {
              margin-left: 8% !important;
            }
          }

          @media (max-width: 991px) {
            .page-wrapper {
              margin-left: 0 !important;
            }
            
            .content {
              margin-left: 0 !important;
            }

            .main-wrapper {
              margin-left: 0 !important;
              width: 100% !important;
            }

            .table-responsive.api-table-wrapper {
              overflow-x: auto !important;
              overflow-y: visible !important;
              -webkit-overflow-scrolling: touch;
            }

            .api-table {
              min-width: 700px !important;
            }
          }

          .bg-light-primary {
            background-color: #f8f9fa !important;
          }

          .border-primary {
            border-color: #6b2ee7 !important;
          }

          @media (max-width: 767px) {
            .page-wrapper {
              margin-left: 0 !important;
              width: 100% !important;
            }

            .content {
              margin-left: 0 !important;
              width: 100% !important;
              padding: 20px 15px !important;
            }

            .card-body .d-flex.align-items-start.justify-content-between {
              flex-direction: column !important;
            }

            .card-body .btn-primary {
              width: 100% !important;
              margin-top: 15px !important;
              margin-left: 0 !important;
            }

            body.slide-nav .sidebar {
              transform: translateX(0) !important;
              visibility: visible !important;
            }

            body:not(.slide-nav) .sidebar {
              transform: translateX(-100%) !important;
            }

            .main-wrapper {
              margin-left: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
            }

            .table-responsive.api-table-wrapper {
              width: 100% !important;
              max-width: 100% !important;
              overflow-x: auto !important;
              overflow-y: visible !important;
              -webkit-overflow-scrolling: touch;
            }

            .api-table {
              min-width: 700px !important;
            }

            .api-table th,
            .api-table td {
              display: table-cell !important;
              white-space: normal !important;
              padding: 8px;
            }

            .card {
              width: 100% !important;
              max-width: 100% !important;
              margin-left: 0 !important;
            }

            .card-body {
              width: 100% !important;
              max-width: 100% !important;
              padding-left: 15px !important;
              padding-right: 15px !important;
            }

            .api-client-name {
              min-width: 150px;
            }
            
            .api-client-id {
              min-width: 120px;
            }

            .api-created-date {
              min-width: 150px;
            }

            .api-status {
              min-width: 80px;
            }

            .api-actions {
              min-width: 60px;
            }

            .text-truncate {
              max-width: 150px;
            }

            .modal {
              padding-left: 0 !important;
            }

            .modal-dialog {
              margin: 10px auto !important;
              max-width: 95% !important;
            }

            .modal-content {
              border-radius: 8px;
            }

            .modal-backdrop {
              margin-left: 0 !important;
            }

            .modal.fade.show {
              margin-left: 0 !important;
              padding-left: 0 !important;
            }

            .modal.fade.show .modal-dialog {
              margin: 10px auto !important;
              max-width: 95% !important;
              width: 95% !important;
            }

            .modal.fade.show .modal-content {
              border-radius: 8px;
              max-height: 90vh;
              overflow-y: auto;
            }

            .modal-body {
              height: auto !important;
              max-height: 70vh !important;
            }

            .avatar-lg {
              width: 48px !important;
              height: 48px !important;
            }

            .avatar-sm {
              width: 32px !important;
              height: 32px !important;
            }

            .settings-sidebar h4 {
              font-size: 16px;
            }

            .page-header .row .col-8,
            .page-header .row .col-4 {
              width: 100%;
              text-align: center;
              margin-bottom: 10px;
            }

            .head-icons {
              justify-content: center;
            }
          }

          @media (max-width: 576px) {
            .page-wrapper {
              margin-left: 0 !important;
              width: 100% !important;
            }

            .content {
              margin-left: 0 !important;
              width: 100% !important;
              padding: 15px 10px !important;
            }

            body.slide-nav .sidebar {
              transform: translateX(0) !important;
              visibility: visible !important;
            }

            body:not(.slide-nav) .sidebar {
              transform: translateX(-100%) !important;
            }

            .main-wrapper {
              margin-left: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
            }

            .table-responsive.api-table-wrapper {
              width: 100% !important;
              max-width: 100% !important;
              overflow-x: auto !important;
              overflow-y: visible !important;
              -webkit-overflow-scrolling: touch;
            }

            .api-table {
              font-size: 12px;
              min-width: 600px !important;
            }

            .api-table th {
              font-size: 11px;
              padding: 6px 4px;
            }

            .api-table td {
              font-size: 11px;
              padding: 6px 4px;
            }

            .card {
              width: 100% !important;
              max-width: 100% !important;
              margin-left: 0 !important;
            }

            .card-body {
              width: 100% !important;
              max-width: 100% !important;
              padding-left: 10px !important;
              padding-right: 10px !important;
            }

            .badge {
              font-size: 9px;
              padding: 2px 4px;
            }

            .avatar-lg {
              width: 40px !important;
              height: 40px !important;
            }

            .avatar-sm {
              width: 28px !important;
              height: 28px !important;
            }

            .api-table .table-action .action-icon {
              width: 28px;
              height: 28px;
            }

            .api-table .table-action .dropdown-menu {
              min-width: 120px;
              font-size: 12px;
            }

            .api-table .table-action .dropdown-item {
              padding: 6px 12px;
              font-size: 12px;
            }

            .modal {
              padding-left: 0 !important;
            }

            .modal-dialog {
              margin: 5px auto !important;
              max-width: 98% !important;
            }

            .modal-content {
              border-radius: 6px;
            }

            .modal-backdrop {
              margin-left: 0 !important;
            }

            .modal.fade.show {
              margin-left: 0 !important;
              padding-left: 0 !important;
            }

            .modal.fade.show .modal-dialog {
              margin: 5px auto !important;
              max-width: 98% !important;
              width: 98% !important;
            }

            .modal.fade.show .modal-content {
              border-radius: 6px;
              max-height: 95vh;
              overflow-y: auto;
            }

            .modal-body {
              height: auto !important;
              max-height: 70vh !important;
            }

            .btn {
              font-size: 12px;
              padding: 6px 12px;
            }

            .form-label {
              font-size: 12px;
            }

            .form-control,
            .form-select {
              font-size: 12px;
            }

            .settings-sidebar h4 {
              font-size: 14px;
            }

            .settings-sidebar .fw-medium {
              font-size: 12px;
            }

            h4.page-title {
              font-size: 18px;
            }

            h4.fs-20 {
              font-size: 16px !important;
            }
          }

          .scope-selector-box {
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
          }

          .scope-selector-box:hover {
            border-color: #6b2ee7 !important;
          }

          .scope-selector-box:focus-within {
            border-color: #6b2ee7 !important;
            box-shadow: 0 0 0 0.2rem rgba(107, 46, 231, 0.15) !important;
          }

          .scope-chip {
            animation: fadeIn 0.2s ease;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          .scope-dropdown-item {
            transition: background-color 0.15s ease;
          }

          .scope-dropdown-item:hover {
            background-color: #f8f9fa !important;
          }
        `}
      </style>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {!isApiAdmin && (
                <div className="card">
                  <div className="card-body pb-0 pt-2">
                    <SettingsTabsNavigation
                      activeTab="api-integration"
                      variant="bottom"
                      includeIcons={true}
                      context="profile"
                    />
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                  <div className="card">
                    <div className="card-body">
                      <div className="settings-sidebar">
                        <h4 className="fw-semibold mb-3">API Integration</h4>
                        <div className="list-group list-group-flush settings-sidebar">
                          <Link
                            to="#"
                            className={`fw-medium ${activeTab === "api-clients" ? "active" : ""
                              }`}
                            onClick={() => setActiveTab("api-clients")}
                          >
                            API Clients
                          </Link>
                          <Link
                            to="#"
                            className={`fw-medium ${activeTab === "usage" ? "active" : ""
                              }`}
                            onClick={() => setActiveTab("usage")}
                          >
                            Usage
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-9 col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      {activeTab === "api-clients" && !detailView && (
                        <>
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <h4 className="fs-20">API Clients</h4>
                            <div>
                              <button
                                className="btn btn-outline-primary mx-2"
                                onClick={() =>
                                  window.open(route.apiDocumentation, "_blank")
                                }
                              >
                                <i className="fa-solid fa-info-circle me-1" />
                                View Documentation
                              </button>
                              <button
                                className="btn btn-primary"
                                onClick={() => setShowCreateModal(true)}
                              >
                                <i className="fa-solid fa-plus me-1" />
                                Create API Client
                              </button>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-12">
                              <div
                                className="table-responsive api-table-wrapper"
                                style={{ position: "relative" }}
                              >
                                <table
                                  className="table table-hover api-table mb-0"
                                  style={{ position: "relative" }}
                                >
                                  <thead>
                                    <tr>
                                      <th className="api-client-name">
                                        Client Name
                                      </th>
                                      <th className="api-client-id">
                                        Client ID
                                      </th>
                                      <th className="api-created-date">
                                        Created Date
                                      </th>
                                      <th className="api-status">Status</th>
                                      <th className="api-actions">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {loading ? (
                                      <tr>
                                        <td
                                          colSpan={5}
                                          className="text-center text-muted py-4"
                                        >
                                          Loading...
                                        </td>
                                      </tr>
                                    ) : apiClients.length === 0 ? (
                                      <tr>
                                        <td
                                          colSpan={5}
                                          className="text-center text-muted py-4"
                                        >
                                          No API clients created yet
                                        </td>
                                      </tr>
                                    ) : (
                                      apiClients.map((client) => (
                                        <tr key={client.api_client_id}>
                                          <td className="api-client-name">
                                            <div className="d-flex align-items-center">
                                              {client.client_logo ? (
                                                <img
                                                  src={client.client_logo}
                                                  alt={client.client_name}
                                                  className="avatar avatar-sm me-2 flex-shrink-0"
                                                />
                                              ) : (
                                                <div className="avatar avatar-sm bg-light me-2 flex-shrink-0">
                                                  <i className="ti ti-api"></i>
                                                </div>
                                              )}
                                              <span
                                                className="text-truncate fw-bold"
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                  handleViewDetails(client)
                                                }
                                              >
                                                {client.client_name}
                                              </span>
                                            </div>
                                          </td>
                                          <td className="api-client-id">
                                            <code className="text-muted small">
                                              {client.client_id}
                                            </code>
                                          </td>
                                          <td className="api-created-date">
                                            <span className="text-nowrap">
                                              {formatCustomDateTime(
                                                client.created_datetime
                                              )}
                                            </span>
                                          </td>
                                          <td className="api-status">
                                            <span
                                              className={`badge ${client.status === "active"
                                                ? "bg-soft-success text-success"
                                                : "bg-soft-danger text-danger"
                                                }`}
                                            >
                                              {client.status}
                                            </span>
                                          </td>
                                          <td className="api-actions">
                                            <div className="dropdown table-action">
                                              <Link
                                                to="#"
                                                className="action-icon"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                              >
                                                <i className="fa fa-ellipsis-v" />
                                              </Link>
                                              <div className="dropdown-menu dropdown-menu-end">
                                                <button
                                                  className="dropdown-item"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    handleViewDetails(client);
                                                  }}
                                                >
                                                  <i className="ti ti-eye text-blue" />
                                                  View Details
                                                </button>
                                                <button
                                                  className="dropdown-item"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    handleEditClick(client);
                                                  }}
                                                >
                                                  <i className="ti ti-edit text-blue" />
                                                  Edit
                                                </button>
                                                <button
                                                  className="dropdown-item"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    handleDeleteApiClient(
                                                      client
                                                    );
                                                  }}
                                                >
                                                  <i className="ti ti-trash text-danger" />
                                                  Delete
                                                </button>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {activeTab === "api-clients" &&
                        detailView &&
                        selectedClient && (
                          <>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                              <div className="d-flex align-items-center">
                                <button
                                  className="btn btn-icon btn-sm me-2 mt-1"
                                  onClick={() => {
                                    setDetailView(false);
                                    setSelectedClient(null);
                                    setShowApiKey(false);
                                  }}
                                >
                                  <i
                                    className="ti ti-arrow-back"
                                    style={{ fontSize: "20px" }}
                                  ></i>
                                </button>
                                <h4 className="fs-20 mb-0">
                                  API Client Details
                                </h4>
                              </div>
                              <button
                                className="btn btn-primary"
                                onClick={() => handleEditClick(selectedClient)}
                              >
                                <i className="ti ti-edit me-1"></i>
                                Edit Client
                              </button>
                            </div>

                            <div className="row mb-4">
                              <div className="col-md-12">
                                <div className="d-flex align-items-center">
                                  {selectedClient.client_logo ? (
                                    <img
                                      src={selectedClient.client_logo}
                                      alt={selectedClient.client_name}
                                      className="avatar avatar-lg me-3"
                                    />
                                  ) : (
                                    <div className="avatar avatar-lg bg-light me-3">
                                      <i
                                        className="ti ti-api"
                                        style={{ fontSize: "32px" }}
                                      ></i>
                                    </div>
                                  )}
                                  <div>
                                    <h4 className="mb-1">
                                      {selectedClient.client_name}
                                    </h4>
                                    <span
                                      className={`badge ${selectedClient.status === "active"
                                        ? "bg-soft-success text-success"
                                        : "bg-soft-danger text-danger"
                                        }`}
                                    >
                                      {selectedClient.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center">
                              <button
                                className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-pill transition-all ${detailTab === "general"
                                  ? "btn-primary text-white shadow-sm"
                                  : "btn-outline-light text-muted border-light-subtle"
                                  }`}
                                onClick={() => setDetailTab("general")}
                                type="button"
                                style={{
                                  minWidth: "fit-content",
                                  fontSize: "0.875rem",
                                  fontWeight:
                                    detailTab === "general" ? "600" : "500",
                                  transform:
                                    detailTab === "general"
                                      ? "translateY(-1px)"
                                      : "none",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <i
                                  className="fa-solid fa-gears me-2"
                                  style={{ fontSize: "1rem" }}
                                ></i>
                                General Settings
                              </button>
                              <button
                                className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-pill transition-all ${detailTab === "webhook"
                                  ? "btn-primary text-white shadow-sm"
                                  : "btn-outline-light text-muted border-light-subtle"
                                  }`}
                                onClick={() => setDetailTab("webhook")}
                                type="button"
                                style={{
                                  minWidth: "fit-content",
                                  fontSize: "0.875rem",
                                  fontWeight:
                                    detailTab === "webhook" ? "600" : "500",
                                  transform:
                                    detailTab === "webhook"
                                      ? "translateY(-1px)"
                                      : "none",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <i
                                  className="ti ti-webhook me-2"
                                  style={{ fontSize: "1rem" }}
                                ></i>
                                Webhook Settings
                              </button>
                            </div>

                            <div className="tab-content">
                              {detailTab === "general" && (
                                <div className="tab-pane fade show active">
                                  <div className="row gy-3 mb-4">
                                    <div className="col-md-6">
                                      <label className="form-label fw-bold">
                                        Client ID
                                        <i
                                          className="ti ti-copy ms-2 text-primary"
                                          style={{
                                            cursor: "pointer",
                                            fontSize: "16px",
                                          }}
                                          onClick={() => {
                                            navigator.clipboard.writeText(
                                              selectedClient.client_id
                                            );
                                            MySwal.fire({
                                              toast: true,
                                              position: "top-end",
                                              icon: "success",
                                              title: "Client ID copied!",
                                              showConfirmButton: false,
                                              timer: 2000,
                                            });
                                          }}
                                        ></i>
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={selectedClient.client_id}
                                        readOnly
                                      />
                                    </div>

                                    <div className="col-md-6">
                                      <label className="form-label fw-bold">
                                        API Key
                                        <i
                                          className="ti ti-copy ms-2 text-primary"
                                          style={{
                                            cursor: "pointer",
                                            fontSize: "16px",
                                          }}
                                          onClick={() => {
                                            navigator.clipboard.writeText(
                                              selectedClient.api_key
                                            );
                                            MySwal.fire({
                                              toast: true,
                                              position: "top-end",
                                              icon: "success",
                                              title: "API Key copied!",
                                              showConfirmButton: false,
                                              timer: 2000,
                                            });
                                          }}
                                        ></i>
                                      </label>
                                      <div className="position-relative">
                                        <input
                                          type="text"
                                          className="form-control"
                                          value={
                                            showApiKey
                                              ? selectedClient.api_key
                                              : "••••••••••••••••••••"
                                          }
                                          readOnly
                                          style={{ paddingRight: "40px" }}
                                        />
                                        <i
                                          className={`ti ${showApiKey ? "ti-eye-off" : "ti-eye"
                                            } text-muted`}
                                          style={{
                                            position: "absolute",
                                            right: "12px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            cursor: "pointer",
                                            fontSize: "18px",
                                          }}
                                          onClick={() =>
                                            setShowApiKey(!showApiKey)
                                          }
                                        ></i>
                                      </div>
                                    </div>

                                    <div className="col-md-6">
                                      <label className="form-label fw-bold">
                                        Expiration Date
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={
                                          selectedClient.expire_datetime
                                            ? formatCustomDateTime(
                                              selectedClient.expire_datetime,
                                              true
                                            )
                                            : "No expiration date provided"
                                        }
                                        readOnly
                                      />
                                    </div>

                                    <div className="col-md-6">
                                      <label className="form-label fw-bold">
                                        Scope Type
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={
                                          selectedClient.scopes &&
                                            selectedClient.scopes.length > 0
                                            ? "Custom"
                                            : "Full Access"
                                        }
                                        readOnly
                                      />
                                    </div>

                                    {selectedClient.scopes &&
                                      selectedClient.scopes.length > 0 && (
                                        <div className="col-md-12">
                                          <label className="form-label fw-bold">
                                            Scopes
                                          </label>
                                          <div
                                            className="form-control"
                                            style={{
                                              minHeight: "45px",
                                              display: "flex",
                                              flexWrap: "wrap",
                                              gap: "6px",
                                              alignItems: "center",
                                              padding: "8px",
                                              backgroundColor: "#f8f9fa",
                                            }}
                                          >
                                            {selectedClient.scopes.map(
                                              (scope) => (
                                                <span
                                                  key={scope.api_scope_id}
                                                  className="badge bg-primary d-inline-flex align-items-center gap-1"
                                                  style={{
                                                    fontSize: "13px",
                                                    padding: "6px 10px",
                                                  }}
                                                >
                                                  {scope.scope_display_name}
                                                </span>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    <div className="col-md-6">
                                      <label className="form-label fw-bold">
                                        Created Date
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={
                                          selectedClient.created_datetime
                                            ? formatCustomDateTime(
                                              selectedClient.created_datetime
                                            )
                                            : "No created date provided"
                                        }
                                        readOnly
                                      />
                                    </div>

                                    <div className="col-md-12">
                                      <label className="form-label fw-bold">
                                        Description
                                      </label>
                                      <textarea
                                        className="form-control"
                                        rows={4}
                                        value={
                                          selectedClient.description ||
                                          "No description provided"
                                        }
                                        readOnly
                                      />
                                    </div>
                                  </div>

                                  <hr className="my-4" />

                                  <div className="row">
                                    <div className="col-md-12">
                                      <div
                                        className="card border-primary bg-light-primary mb-0"
                                        style={{
                                          borderWidth: "2px",
                                          borderStyle: "dashed",
                                        }}
                                      >
                                        <div className="card-body">
                                          <div className="d-flex align-items-start justify-content-between">
                                            <div className="flex-grow-1">
                                              <h5 className="mb-2 d-flex align-items-center">
                                                <i
                                                  className="ti ti-refresh-alert text-primary me-2"
                                                  style={{ fontSize: "24px" }}
                                                ></i>
                                                Regenerate API Secret
                                              </h5>
                                              <p
                                                className="text-muted mb-0"
                                                style={{ fontSize: "14px" }}
                                              >
                                                Generate a new API secret for
                                                enhanced security. The previous
                                                secret will be immediately
                                                invalidated.
                                              </p>
                                            </div>
                                            <button
                                              type="button"
                                              className="btn btn-primary ms-3"
                                              style={{
                                                minWidth: "140px",
                                                fontWeight: "600",
                                                boxShadow:
                                                  "0 4px 8px rgba(107, 46, 231, 0.3)",
                                              }}
                                              onClick={() => {
                                                handleRegenerateSecret(
                                                  selectedClient.api_key,
                                                  selectedClient.client_name
                                                );
                                              }}
                                              disabled={loading}
                                            >
                                              {loading ? (
                                                <>
                                                  <span className="spinner-border spinner-border-sm me-2" />
                                                  Processing...
                                                </>
                                              ) : (
                                                <>
                                                  <i className="ti ti-refresh me-1"></i>
                                                  Regenerate
                                                </>
                                              )}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {detailTab === "webhook" && (
                                <div className="tab-pane fade show active">
                                  <div className="d-flex align-items-center justify-content-between mb-4">
                                    <h5 className="mb-0">
                                      Webhook Subscriptions
                                    </h5>
                                    <button
                                      className="btn btn-primary"
                                      onClick={() => {
                                        setIsEditingWebhook(false);
                                        resetWebhookForm();
                                        setShowWebhookModal(true);
                                      }}
                                    >
                                      <i className="fa-solid fa-plus me-1"></i>
                                      Add Webhook
                                    </button>
                                  </div>

                                  {webhookLoading ? (
                                    <div className="text-center py-5">
                                      <div
                                        className="spinner-border text-primary"
                                        role="status"
                                      >
                                        <span className="visually-hidden">
                                          Loading...
                                        </span>
                                      </div>
                                    </div>
                                  ) : webhookSubscriptions.length === 0 ? (
                                    <div className="text-center py-5">
                                      <i
                                        className="ti ti-webhook text-muted"
                                        style={{ fontSize: "64px" }}
                                      ></i>
                                      <h5 className="text-muted mt-3">
                                        No Webhook Subscriptions
                                      </h5>
                                      <p className="text-muted">
                                        Create your first webhook subscription
                                        to get started
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="table-responsive">
                                      <table className="table table-hover">
                                        <thead>
                                          <tr>
                                            <th>Webhook URL</th>
                                            <th>Status</th>
                                            <th>Event Mode</th>
                                            <th>Created</th>
                                            <th>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {webhookSubscriptions.map(
                                            (webhook) => (
                                              <tr
                                                key={
                                                  webhook.api_webhook_subscription_id
                                                }
                                              >
                                                <td>
                                                  <div className="d-flex align-items-center gap-2">
                                                    <code
                                                      className="text-muted small"
                                                      title={
                                                        webhook.webhook_url
                                                      }
                                                    >
                                                      {webhook.webhook_url
                                                        .length > 20
                                                        ? `${webhook.webhook_url.substring(
                                                          0,
                                                          20
                                                        )}...`
                                                        : webhook.webhook_url}
                                                    </code>
                                                    <button
                                                      className="btn btn-sm btn-outline-primary p-1"
                                                      onClick={() => {
                                                        navigator.clipboard.writeText(
                                                          webhook.webhook_url
                                                        );
                                                        MySwal.fire({
                                                          title: "Copied!",
                                                          text: "Webhook URL copied to clipboard",
                                                          icon: "success",
                                                          timer: 1500,
                                                          showConfirmButton:
                                                            false,
                                                        });
                                                      }}
                                                      title="Copy URL"
                                                      style={{ lineHeight: 1 }}
                                                    >
                                                      <i
                                                        className="ti ti-copy"
                                                        style={{
                                                          fontSize: "14px",
                                                        }}
                                                      ></i>
                                                    </button>
                                                  </div>
                                                </td>
                                                <td>
                                                  <span
                                                    className={`badge ${webhook.status ===
                                                      "active"
                                                      ? "bg-soft-success text-success"
                                                      : "bg-soft-danger text-danger"
                                                      }`}
                                                  >
                                                    {webhook.status}
                                                  </span>
                                                </td>
                                                <td>
                                                  <span className="badge bg-soft-info text-info">
                                                    {webhook.webhook_event_mode ===
                                                      "global"
                                                      ? "All Events"
                                                      : "Custom"}
                                                  </span>
                                                </td>
                                                <td className="text-nowrap">
                                                  {formatCustomDateTime(
                                                    webhook.created_datetime
                                                  )}
                                                </td>
                                                <td>
                                                  <div className="d-flex gap-2">
                                                    <button
                                                      className="btn btn-sm btn-outline-primary"
                                                      onClick={() =>
                                                        handleEditWebhook(
                                                          webhook
                                                        )
                                                      }
                                                      title="Edit"
                                                    >
                                                      <i className="ti ti-edit"></i>
                                                    </button>
                                                    <button
                                                      className="btn btn-sm btn-outline-danger"
                                                      onClick={() =>
                                                        handleDeleteWebhook(
                                                          webhook
                                                        )
                                                      }
                                                      title="Delete"
                                                    >
                                                      <i className="ti ti-trash"></i>
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            )
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        )}

                      {activeTab === "usage" && (
                        <>
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <h4 className="fs-20">Usage Statistics</h4>
                          </div>

                          <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center ">
                            <button
                              className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-pill transition-all ${usageTab === "api-usage"
                                ? "btn-primary text-white shadow-sm"
                                : "btn-outline-light text-muted border-light-subtle"
                                }`}
                              onClick={() => setUsageTab("api-usage")}
                              type="button"
                              style={{
                                minWidth: "fit-content",
                                fontSize: "0.875rem",
                                fontWeight:
                                  usageTab === "api-usage" ? "600" : "500",
                                transform:
                                  usageTab === "api-usage"
                                    ? "translateY(-1px)"
                                    : "none",
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              <i
                                className="ti ti-api me-2"
                                style={{ fontSize: "1rem" }}
                              ></i>
                              API Usage
                            </button>
                            <button
                              className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-pill transition-all ${usageTab === "webhook-usage"
                                ? "btn-primary text-white shadow-sm"
                                : "btn-outline-light text-muted border-light-subtle"
                                }`}
                              onClick={() => setUsageTab("webhook-usage")}
                              type="button"
                              style={{
                                minWidth: "fit-content",
                                fontSize: "0.875rem",
                                fontWeight:
                                  usageTab === "webhook-usage" ? "600" : "500",
                                transform:
                                  usageTab === "webhook-usage"
                                    ? "translateY(-1px)"
                                    : "none",
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              <i
                                className="ti ti-webhook me-2"
                                style={{ fontSize: "1rem" }}
                              ></i>
                              Webhook Usage
                            </button>
                          </div>

                          <div className="tab-content">
                            {usageTab === "api-usage" && (
                              <div className="tab-pane fade show active">
                                <div className="row">
                                  <div className="col-md-12">
                                    <div className="row gy-3 mb-4">
                                      <div className="col-md-4">
                                        <div className="card bg-light-info">
                                          <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                              <div>
                                                <h6 className="text-muted mb-2">
                                                  Total Requests
                                                </h6>
                                                <h3 className="mb-0">
                                                  {getAuditStats().total}
                                                </h3>
                                              </div>
                                              <div className="avatar avatar-lg bg-info">
                                                <i
                                                  className="ti ti-api"
                                                  style={{ fontSize: "24px" }}
                                                ></i>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-4">
                                        <div className="card bg-light-success">
                                          <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                              <div>
                                                <h6 className="text-muted mb-2">
                                                  Successful
                                                </h6>
                                                <h3 className="mb-0">
                                                  {getAuditStats().successful}
                                                </h3>
                                              </div>
                                              <div className="avatar avatar-lg bg-success">
                                                <i
                                                  className="ti ti-check"
                                                  style={{ fontSize: "24px" }}
                                                ></i>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-4">
                                        <div className="card bg-light-danger">
                                          <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                              <div>
                                                <h6 className="text-muted mb-2">
                                                  Failed
                                                </h6>
                                                <h3 className="mb-0">
                                                  {getAuditStats().failed}
                                                </h3>
                                              </div>
                                              <div className="avatar avatar-lg bg-danger">
                                                <i
                                                  className="ti ti-x"
                                                  style={{ fontSize: "24px" }}
                                                ></i>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <UsageFilters
                                      dateRange={[
                                        apiUsageStartDate,
                                        apiUsageEndDate,
                                      ]}
                                      selectedClientId={apiUsageClientId}
                                      onDateRangeChange={(start, end) => {
                                        setApiUsageStartDate(start);
                                        setApiUsageEndDate(end);
                                      }}
                                      onClientChange={setApiUsageClientId}
                                    />
                                    <UsageChart
                                      type="api"
                                      startDate={apiUsageStartDate}
                                      endDate={apiUsageEndDate}
                                      apiClientId={apiUsageClientId}
                                    />
                                    <div className="table-responsive">
                                      <table className="table table-hover">
                                        <thead>
                                          <tr>
                                            <th>Timestamp</th>
                                            <th>Endpoint</th>
                                            <th>Method</th>
                                            <th>Client</th>
                                            <th>Status</th>
                                            <th>Response Time</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {auditLogsLoading ? (
                                            <tr>
                                              <td
                                                colSpan={6}
                                                className="text-center text-muted py-4"
                                              >
                                                Loading...
                                              </td>
                                            </tr>
                                          ) : auditLogs.length === 0 ? (
                                            <tr>
                                              <td
                                                colSpan={6}
                                                className="text-center text-muted py-4"
                                              >
                                                No API usage data available
                                              </td>
                                            </tr>
                                          ) : (
                                            auditLogs.map((log) => (
                                              <tr key={log.api_audit_log_id}>
                                                <td className="text-nowrap">
                                                  {formatCustomDateTime(
                                                    log.request_datetime
                                                  )}
                                                </td>
                                                <td>
                                                  <code className="text-muted small">
                                                    {log.end_point}
                                                  </code>
                                                </td>
                                                <td>
                                                  <span
                                                    className={`badge ${log.http_method === "GET"
                                                      ? "bg-soft-info text-info"
                                                      : log.http_method ===
                                                        "POST"
                                                        ? "bg-soft-success text-success"
                                                        : log.http_method ===
                                                          "PUT" ||
                                                          log.http_method ===
                                                          "PATCH"
                                                          ? "bg-soft-warning text-warning"
                                                          : log.http_method ===
                                                            "DELETE"
                                                            ? "bg-soft-danger text-danger"
                                                            : "bg-soft-secondary text-secondary"
                                                      }`}
                                                  >
                                                    {log.http_method}
                                                  </span>
                                                </td>
                                                <td>
                                                  {log.api_client ? (
                                                    <span
                                                      className="text-primary fw-medium"
                                                      style={{
                                                        cursor: "pointer",
                                                        textDecoration:
                                                          "underline",
                                                      }}
                                                      onClick={() =>
                                                        handleClientNameClick(
                                                          log.api_client
                                                            .client_id
                                                        )
                                                      }
                                                    >
                                                      {
                                                        log.api_client
                                                          .client_name
                                                      }
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted">
                                                      N/A
                                                    </span>
                                                  )}
                                                </td>
                                                <td>
                                                  <span
                                                    className={`badge ${log.status_code >= 200 &&
                                                      log.status_code < 300
                                                      ? "bg-soft-success text-success"
                                                      : log.status_code >= 400
                                                        ? "bg-soft-danger text-danger"
                                                        : "bg-soft-warning text-warning"
                                                      }`}
                                                  >
                                                    {log.status_code}
                                                  </span>
                                                </td>
                                                <td>
                                                  {log.response_time_ms} ms
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                    {!auditLogsLoading &&
                                      auditLogs.length > 0 && (
                                        <TablePagination
                                          currentPage={auditLogsPage}
                                          pageSize={12}
                                          totalItems={auditLogsTotalCount}
                                          filteredItems={auditLogsTotalCount}
                                          isFiltered={false}
                                          onPageChange={
                                            handleAuditLogsPageChange
                                          }
                                          onPageSizeChange={() => { }}
                                          entityName="API Usage Logs"
                                          loading={auditLogsLoading}
                                        />
                                      )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {usageTab === "webhook-usage" && (
                              <div className="tab-pane fade show active">
                                <div className="row">
                                  <div className="col-md-12">
                                    <div className="row gy-3 mb-4">
                                      <div className="col-md-4">
                                        <div className="card bg-light-info">
                                          <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                              <div>
                                                <h6 className="text-muted mb-2">
                                                  Total Requests
                                                </h6>
                                                <h3 className="mb-0">
                                                  {
                                                    getWebhookDeliveryStats()
                                                      .total
                                                  }
                                                </h3>
                                              </div>
                                              <div className="avatar avatar-lg bg-info">
                                                <i
                                                  className="ti ti-webhook"
                                                  style={{ fontSize: "24px" }}
                                                ></i>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-4">
                                        <div className="card bg-light-success">
                                          <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                              <div>
                                                <h6 className="text-muted mb-2">
                                                  Successful
                                                </h6>
                                                <h3 className="mb-0">
                                                  {
                                                    getWebhookDeliveryStats()
                                                      .successful
                                                  }
                                                </h3>
                                              </div>
                                              <div className="avatar avatar-lg bg-success">
                                                <i
                                                  className="ti ti-check"
                                                  style={{ fontSize: "24px" }}
                                                ></i>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-4">
                                        <div className="card bg-light-danger">
                                          <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                              <div>
                                                <h6 className="text-muted mb-2">
                                                  Failed
                                                </h6>
                                                <h3 className="mb-0">
                                                  {
                                                    getWebhookDeliveryStats()
                                                      .failed
                                                  }
                                                </h3>
                                              </div>
                                              <div className="avatar avatar-lg bg-danger">
                                                <i
                                                  className="ti ti-x"
                                                  style={{ fontSize: "24px" }}
                                                ></i>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <UsageFilters
                                      dateRange={[
                                        webhookUsageStartDate,
                                        webhookUsageEndDate,
                                      ]}
                                      selectedClientId={webhookUsageClientId}
                                      onDateRangeChange={(start, end) => {
                                        setWebhookUsageStartDate(start);
                                        setWebhookUsageEndDate(end);
                                      }}
                                      onClientChange={setWebhookUsageClientId}
                                    />
                                    <UsageChart
                                      type="webhook"
                                      startDate={webhookUsageStartDate}
                                      endDate={webhookUsageEndDate}
                                      apiClientId={webhookUsageClientId}
                                    />
                                    <div className="table-responsive">
                                      <table className="table table-hover">
                                        <thead>
                                          <tr>
                                            <th>Timestamp</th>
                                            <th>Endpoint</th>
                                            <th>Method</th>
                                            <th>Client</th>
                                            <th>Status</th>
                                            <th>Response Time</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {webhookDeliveryLogsLoading ? (
                                            <tr>
                                              <td
                                                colSpan={6}
                                                className="text-center text-muted py-4"
                                              >
                                                Loading...
                                              </td>
                                            </tr>
                                          ) : webhookDeliveryLogs.length ===
                                            0 ? (
                                            <tr>
                                              <td
                                                colSpan={6}
                                                className="text-center text-muted py-4"
                                              >
                                                No webhook delivery data
                                                available
                                              </td>
                                            </tr>
                                          ) : (
                                            webhookDeliveryLogs.map((log) => (
                                              <tr
                                                key={
                                                  log.api_webhook_delivery_audit_log_id
                                                }
                                              >
                                                <td className="text-nowrap">
                                                  {formatCustomDateTime(
                                                    log.last_attempt_datetime
                                                  )}
                                                </td>
                                                <td>
                                                  {log.api_webhook_subscription
                                                    ?.webhook_url ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                      <code
                                                        className="text-muted small"
                                                        title={
                                                          log
                                                            .api_webhook_subscription
                                                            .webhook_url
                                                        }
                                                      >
                                                        {log
                                                          .api_webhook_subscription
                                                          .webhook_url.length >
                                                          20
                                                          ? `${log.api_webhook_subscription.webhook_url.substring(
                                                            0,
                                                            20
                                                          )}...`
                                                          : log
                                                            .api_webhook_subscription
                                                            .webhook_url}
                                                      </code>
                                                      <button
                                                        className="btn btn-sm btn-outline-primary p-1"
                                                        onClick={() => {
                                                          navigator.clipboard.writeText(
                                                            log
                                                              .api_webhook_subscription
                                                              .webhook_url
                                                          );
                                                          MySwal.fire({
                                                            title: "Copied!",
                                                            text: "Endpoint URL copied to clipboard",
                                                            icon: "success",
                                                            timer: 1500,
                                                            showConfirmButton:
                                                              false,
                                                          });
                                                        }}
                                                        title="Copy URL"
                                                        style={{
                                                          lineHeight: 1,
                                                        }}
                                                      >
                                                        <i
                                                          className="ti ti-copy"
                                                          style={{
                                                            fontSize: "14px",
                                                          }}
                                                        ></i>
                                                      </button>
                                                    </div>
                                                  ) : (
                                                    <code className="text-muted small">
                                                      N/A
                                                    </code>
                                                  )}
                                                </td>
                                                <td>
                                                  <span
                                                    className={`badge ${extractHttpMethodFromPayload(
                                                      log.payload
                                                    ) === "GET"
                                                      ? "bg-soft-info text-info"
                                                      : extractHttpMethodFromPayload(
                                                        log.payload
                                                      ) === "POST"
                                                        ? "bg-soft-success text-success"
                                                        : extractHttpMethodFromPayload(
                                                          log.payload
                                                        ) === "PUT" ||
                                                          extractHttpMethodFromPayload(
                                                            log.payload
                                                          ) === "PATCH"
                                                          ? "bg-soft-warning text-warning"
                                                          : extractHttpMethodFromPayload(
                                                            log.payload
                                                          ) === "DELETE"
                                                            ? "bg-soft-danger text-danger"
                                                            : "bg-soft-secondary text-secondary"
                                                      }`}
                                                  >
                                                    {extractHttpMethodFromPayload(
                                                      log.payload
                                                    )}
                                                  </span>
                                                </td>
                                                <td>
                                                  {log.api_client ? (
                                                    <span
                                                      className="text-primary fw-medium"
                                                      style={{
                                                        cursor: "pointer",
                                                        textDecoration:
                                                          "underline",
                                                      }}
                                                      onClick={() =>
                                                        handleClientNameClick(
                                                          log.api_client
                                                            .api_client_id
                                                        )
                                                      }
                                                    >
                                                      {
                                                        log.api_client
                                                          .client_name
                                                      }
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted">
                                                      N/A
                                                    </span>
                                                  )}
                                                </td>
                                                <td>
                                                  <span
                                                    className={`badge ${log.delivery_status ===
                                                      "success"
                                                      ? "bg-soft-success text-success"
                                                      : log.delivery_status ===
                                                        "error"
                                                        ? "bg-soft-danger text-danger"
                                                        : "bg-soft-warning text-warning"
                                                      }`}
                                                  >
                                                    {log.delivery_status}
                                                  </span>
                                                </td>
                                                <td>
                                                  {log.response_time_ms} ms
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                    {!webhookDeliveryLogsLoading &&
                                      webhookDeliveryLogs.length > 0 && (
                                        <TablePagination
                                          currentPage={webhookDeliveryLogsPage}
                                          pageSize={12}
                                          totalItems={
                                            webhookDeliveryLogsTotalCount
                                          }
                                          filteredItems={
                                            webhookDeliveryLogsTotalCount
                                          }
                                          isFiltered={false}
                                          onPageChange={
                                            handleWebhookDeliveryLogsPageChange
                                          }
                                          onPageSizeChange={() => { }}
                                          entityName="Webhook Delivery Logs"
                                          loading={webhookDeliveryLogsLoading}
                                        />
                                      )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEditModal && selectedClient && (
        <>
          <div
            className="modal fade show api-edit-modal"
            style={{ display: "block" }}
            role="dialog"
            onClick={() => {
              setShowEditModal(false);
              if (!detailView) setSelectedClient(null);
              resetScopeDropdownState();
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "600px", width: "600px" }}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header border-bottom">
                  <h4>Edit API Client</h4>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowEditModal(false);
                      if (!detailView) setSelectedClient(null);
                      resetScopeDropdownState();
                    }}
                    aria-label="Close"
                  />
                </div>
                <div
                  className="modal-body"
                  style={{
                    maxHeight: "calc(100vh - 200px)",
                    overflowY: "auto",
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label">Client Logo</label>
                    {editFormData.existing_logo &&
                      !editFormData.client_logo && (
                        <div className="mb-2">
                          <img
                            src={editFormData.existing_logo}
                            alt="Current logo"
                            className="avatar avatar-md"
                          />
                          <small className="text-muted d-block mt-1">
                            Current logo
                          </small>
                        </div>
                      )}
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleEditFileChange}
                    />
                    {editFormData.client_logo && (
                      <small className="text-muted mt-1 d-block">
                        New logo selected: {editFormData.client_logo.name}
                      </small>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Client Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="client_name"
                      value={editFormData.client_name}
                      onChange={handleEditInputChange}
                      placeholder="Enter client name"
                    />
                  </div>


                  {!isApiAdmin && (<>

                    <div className="mb-3">
                      <label className="form-label">
                        Scope Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="scope_mode"
                        value={editFormData.scope_mode}
                        onChange={(e) => {
                          handleEditInputChange(e);
                          if (e.target.value === "global") {
                            setSelectedScopes([]);
                          }
                        }}
                      >
                        <option value="custom">Custom</option>
                        <option value="global">Full Access</option>
                      </select>
                    </div>
                    {editFormData.scope_mode === "custom" && (
                      <div className="mb-3">
                        <label className="form-label">Scopes (Optional)</label>
                        <div className="position-relative">
                          <div
                            className="form-control scope-selector-box"
                            style={{
                              minHeight: "45px",
                              cursor: "pointer",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                              alignItems: "center",
                              padding: "8px",
                            }}
                            onClick={() =>
                              setShowScopeDropdown(!showScopeDropdown)
                            }
                          >
                            {selectedScopes.length === 0 ? (
                              <span className="text-muted">Select scopes...</span>
                            ) : (
                              selectedScopes.map((scopeId) => {
                                const scope = selectedScopesData.find(
                                  (s) => s.api_scope_id === scopeId
                                ) || availableScopes.find(
                                  (s) => s.api_scope_id === scopeId
                                );
                                return scope ? (
                                  <span
                                    key={scopeId}
                                    className="badge bg-primary d-inline-flex align-items-center gap-1 scope-chip"
                                    style={{
                                      fontSize: "13px",
                                      padding: "6px 10px",
                                    }}
                                  >
                                    {scope.scope_display_name}
                                    <i
                                      className="ti ti-x"
                                      style={{
                                        fontSize: "14px",
                                        cursor: "pointer",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeScope(scopeId);
                                      }}
                                    ></i>
                                  </span>
                                ) : null;
                              })
                            )}
                          </div>
                          {showScopeDropdown && (
                            <>
                              <div
                                className="position-fixed"
                                style={{
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  zIndex: 1040,
                                }}
                                onClick={() => {
                                  setShowScopeDropdown(false);
                                  setScopeSearchQuery("");
                                  setScopesPage(1);
                                  fetchApiScopes(1, "", false);
                                }}
                              />
                              <div
                                className="position-absolute w-100 bg-white border rounded shadow-lg"
                                style={{
                                  zIndex: 1050,
                                  top: "calc(100% + 4px)",
                                }}
                              >
                                <div
                                  className="p-2 border-bottom"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search scopes..."
                                    value={scopeSearchQuery}
                                    onChange={(e) =>
                                      handleScopeSearch(e.target.value)
                                    }
                                    autoFocus
                                  />
                                </div>
                                <div
                                  style={{
                                    maxHeight: "250px",
                                    overflowY: "auto",
                                  }}
                                  onScroll={(e) => {
                                    const target = e.currentTarget;
                                    const {
                                      scrollTop,
                                      scrollHeight,
                                      clientHeight,
                                    } = target;
                                    if (
                                      scrollHeight - scrollTop <=
                                      clientHeight + 5 &&
                                      scopesHasMore &&
                                      !isLoadingMoreScopes
                                    ) {
                                      loadMoreScopes();
                                    }
                                  }}
                                >
                                  {availableScopes.length === 0 ? (
                                    <div className="p-3 text-center text-muted">
                                      {scopeSearchQuery
                                        ? "No scopes found"
                                        : "No scopes available"}
                                    </div>
                                  ) : (
                                    <>
                                      {availableScopes.map((scope) => (
                                        <div
                                          key={scope.api_scope_id}
                                          className={`p-3 cursor-pointer border-bottom scope-dropdown-item ${selectedScopes.includes(
                                            scope.api_scope_id
                                          )
                                            ? "bg-light"
                                            : ""
                                            }`}
                                          style={{ cursor: "pointer" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleScope(scope.api_scope_id);
                                          }}
                                        >
                                          <div className="d-flex align-items-start justify-content-between">
                                            <div className="flex-grow-1">
                                              <div className="d-flex align-items-center gap-2">
                                                <input
                                                  type="checkbox"
                                                  checked={selectedScopes.includes(
                                                    scope.api_scope_id
                                                  )}
                                                  onChange={() => { }}
                                                  style={{ cursor: "pointer" }}
                                                />
                                                <strong>
                                                  {scope.scope_display_name}
                                                </strong>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                      {isLoadingMoreScopes && (
                                        <div className="p-3 text-center text-muted">
                                          <small>Loading more...</small>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <small className="text-muted">
                          Note: Changing scopes requires regenerating the API
                          secret
                        </small>
                      </div>
                    )}
                  </>)}
                  <div className="mb-3">
                    <label className="form-label">
                      Expiration Date {isApiAdmin ? <span className="text-danger">*</span> : "(Optional)"}
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="expire_datetime"
                      value={
                        editFormData.expire_datetime
                          ? editFormData.expire_datetime.split("T")[0]
                          : ""
                      }
                      onChange={handleEditInputChange}
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={editFormData.description}
                      onChange={handleEditInputChange}
                      placeholder="Enter description"
                      rows={3}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditInputChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      setShowEditModal(false);
                      if (!detailView) setSelectedClient(null);
                      resetScopeDropdownState();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUpdateApiClient}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check me-1" />
                        Update Client
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            className="modal-backdrop fade show"
            onClick={() => {
              setShowEditModal(false);
              if (!detailView) setSelectedClient(null);
              resetScopeDropdownState();
            }}
          />
        </>
      )}

      {showCreateModal && (
        <>
          <div
            className="modal fade show api-create-modal"
            style={{ display: "block" }}
            role="dialog"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
              resetScopeDropdownState();
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "600px", width: "600px" }}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header border-bottom">
                  <h4>Create API Client</h4>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                      resetScopeDropdownState();
                    }}
                    aria-label="Close"
                  />
                </div>
                <div
                  className="modal-body"
                  style={{
                    maxHeight: "calc(100vh - 200px)",
                    overflowY: "auto",
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label">Client Logo</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                    {formData.client_logo && (
                      <small className="text-muted mt-1 d-block">
                        Selected: {formData.client_logo.name}
                      </small>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Client Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="client_name"
                      value={formData.client_name}
                      onChange={handleInputChange}
                      placeholder="Enter client name"
                    />
                  </div>
                  {!isApiAdmin && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">
                          Scope Type <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          name="scope_mode"
                          value={formData.scope_mode}
                          onChange={(e) => {
                            handleInputChange(e);
                            if (e.target.value === "global") {
                              setSelectedScopes([]);
                            }
                          }}
                        >
                          <option value="custom">Custom</option>
                          <option value="global">Full Access</option>
                        </select>
                      </div>
                      {formData.scope_mode === "custom" && (
                        <div className="mb-3">
                          <label className="form-label">Scopes (Optional)</label>
                          <div className="position-relative">
                            <div
                              className="form-control scope-selector-box"
                              style={{
                                minHeight: "45px",
                                cursor: "pointer",
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "6px",
                                alignItems: "center",
                                padding: "8px",
                              }}
                              onClick={() =>
                                setShowScopeDropdown(!showScopeDropdown)
                              }
                            >
                              {selectedScopes.length === 0 ? (
                                <span className="text-muted">Select scopes...</span>
                              ) : (
                                selectedScopes.map((scopeId) => {
                                  const scope = selectedScopesData.find(
                                    (s) => s.api_scope_id === scopeId
                                  ) || availableScopes.find(
                                    (s) => s.api_scope_id === scopeId
                                  );
                                  return scope ? (
                                    <span
                                      key={scopeId}
                                      className="badge bg-primary d-inline-flex align-items-center gap-1 scope-chip"
                                      style={{
                                        fontSize: "13px",
                                        padding: "6px 10px",
                                      }}
                                    >
                                      {scope.scope_display_name}
                                      <i
                                        className="ti ti-x"
                                        style={{
                                          fontSize: "14px",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeScope(scopeId);
                                        }}
                                      ></i>
                                    </span>
                                  ) : null;
                                })
                              )}
                            </div>
                            {showScopeDropdown && (
                              <>
                                <div
                                  className="position-fixed"
                                  style={{
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 1040,
                                  }}
                                  onClick={() => {
                                    setShowScopeDropdown(false);
                                    setScopeSearchQuery("");
                                    setScopesPage(1);
                                    fetchApiScopes(1, "", false);
                                  }}
                                />
                                <div
                                  className="position-absolute w-100 bg-white border rounded shadow-lg"
                                  style={{
                                    zIndex: 1050,
                                    top: "calc(100% + 4px)",
                                  }}
                                >
                                  <div
                                    className="p-2 border-bottom"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder="Search scopes..."
                                      value={scopeSearchQuery}
                                      onChange={(e) =>
                                        handleScopeSearch(e.target.value)
                                      }
                                      autoFocus
                                    />
                                  </div>
                                  <div
                                    style={{
                                      maxHeight: "250px",
                                      overflowY: "auto",
                                    }}
                                    onScroll={(e) => {
                                      const target = e.currentTarget;
                                      const {
                                        scrollTop,
                                        scrollHeight,
                                        clientHeight,
                                      } = target;
                                      if (
                                        scrollHeight - scrollTop <=
                                        clientHeight + 5 &&
                                        scopesHasMore &&
                                        !isLoadingMoreScopes
                                      ) {
                                        loadMoreScopes();
                                      }
                                    }}
                                  >
                                    {availableScopes.length === 0 ? (
                                      <div className="p-3 text-center text-muted">
                                        {scopeSearchQuery
                                          ? "No scopes found"
                                          : "No scopes available"}
                                      </div>
                                    ) : (
                                      <>
                                        {availableScopes.map((scope) => (
                                          <div
                                            key={scope.api_scope_id}
                                            className={`p-3 cursor-pointer border-bottom scope-dropdown-item ${selectedScopes.includes(
                                              scope.api_scope_id
                                            )
                                              ? "bg-light"
                                              : ""
                                              }`}
                                            style={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleScope(scope.api_scope_id);
                                            }}
                                          >
                                            <div className="d-flex align-items-start justify-content-between">
                                              <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2">
                                                  <input
                                                    type="checkbox"
                                                    checked={selectedScopes.includes(
                                                      scope.api_scope_id
                                                    )}
                                                    onChange={() => { }}
                                                    style={{ cursor: "pointer" }}
                                                  />
                                                  <strong>
                                                    {scope.scope_display_name}
                                                  </strong>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                        {isLoadingMoreScopes && (
                                          <div className="p-3 text-center text-muted">
                                            <small>Loading more...</small>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                          <small className="text-muted">
                            Select the API scopes this client will have access to
                          </small>
                        </div>
                      )}
                    </>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter description"
                      rows={3}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Expiration Date {isApiAdmin ? <span className="text-danger">*</span> : "(Optional)"}
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="expire_datetime"
                      value={formData.expire_datetime}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                      resetScopeDropdownState();
                    }}
                  >
                    <i className="ti ti-x me-1"></i>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreateApiClient}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-plus me-1" />
                        Create Client
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            className="modal-backdrop fade show"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
              resetScopeDropdownState();
            }}
          />
        </>
      )}

      {showWebhookModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            role="dialog"
            onClick={() => {
              setShowWebhookModal(false);
              setIsEditingWebhook(false);
              setSelectedWebhook(null);
              resetWebhookForm();
              resetWebhookEventDropdownState();
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "600px", width: "600px" }}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header border-bottom">
                  <h4>
                    {isEditingWebhook ? "Edit" : "Create"} Webhook Subscription
                  </h4>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowWebhookModal(false);
                      setIsEditingWebhook(false);
                      setSelectedWebhook(null);
                      resetWebhookForm();
                      resetWebhookEventDropdownState();
                    }}
                    aria-label="Close"
                  />
                </div>
                <div
                  className="modal-body"
                  style={{
                    maxHeight: "calc(100vh - 200px)",
                    overflowY: "auto",
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label">
                      Webhook URL <span className="text-danger">*</span>
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      name="webhook_url"
                      value={webhookFormData.webhook_url}
                      onChange={handleWebhookInputChange}
                      placeholder="https://example.com/webhook"
                    />
                    {/* URL Validation Status */}
                    {urlValidationStatus !== "idle" && (
                      <div className="mt-2">
                        {urlValidationStatus === "validating" && (
                          <div className="d-flex align-items-center text-muted">
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <small>Validating URL...</small>
                          </div>
                        )}
                        {urlValidationStatus === "valid" && (
                          <div className="d-flex align-items-center text-success">
                            <i
                              className="ti ti-circle-check me-2"
                              style={{ fontSize: "18px" }}
                            ></i>
                            <small>URL is valid</small>
                          </div>
                        )}
                        {urlValidationStatus === "invalid" && (
                          <div className="d-flex align-items-center text-danger">
                            <i
                              className="ti ti-circle-x me-2"
                              style={{ fontSize: "18px" }}
                            ></i>
                            <small>URL is invalid</small>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label mb-0">Webhook Success URL</label>
                      {webhookFormData.webhook_success_url && (
                        <button
                          className="btn btn-sm btn-outline-primary p-1"
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(webhookFormData.webhook_success_url);
                            MySwal.fire({
                              title: "Copied!",
                              text: "Success URL copied to clipboard",
                              icon: "success",
                              timer: 1500,
                              showConfirmButton: false,
                            });
                          }}
                          title="Copy Success URL"
                          style={{ lineHeight: 1 }}
                        >
                          <i className="ti ti-copy" style={{ fontSize: "14px" }}></i>
                        </button>
                      )}
                    </div>
                    <input
                      type="url"
                      className="form-control"
                      name="webhook_success_url"
                      value={webhookFormData.webhook_success_url}
                      onChange={handleWebhookInputChange}
                      placeholder="https://example.com/success"
                    />
                  </div>

                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label mb-0">Webhook Failure URL</label>
                      {webhookFormData.webhook_failure_url && (
                        <button
                          className="btn btn-sm btn-outline-primary p-1"
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(webhookFormData.webhook_failure_url);
                            MySwal.fire({
                              title: "Copied!",
                              text: "Failure URL copied to clipboard",
                              icon: "success",
                              timer: 1500,
                              showConfirmButton: false,
                            });
                          }}
                          title="Copy Failure URL"
                          style={{ lineHeight: 1 }}
                        >
                          <i className="ti ti-copy" style={{ fontSize: "14px" }}></i>
                        </button>
                      )}
                    </div>
                    <input
                      type="url"
                      className="form-control"
                      name="webhook_failure_url"
                      value={webhookFormData.webhook_failure_url}
                      onChange={handleWebhookInputChange}
                      placeholder="https://example.com/failure"
                    />
                  </div> */}

                  <div className="mb-3">
                    <label className="form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="status"
                      value={webhookFormData.status}
                      onChange={handleWebhookInputChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Webhook Event Mode <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="webhook_event_mode"
                      value={webhookFormData.webhook_event_mode}
                      onChange={(e) => {
                        handleWebhookInputChange(e);
                        if (e.target.value === "global") {
                          setSelectedWebhookEvents([]);
                          setSelectedWebhookEventsData([]);
                        }
                      }}
                    >
                      <option value="custom">Custom</option>
                      <option value="global">All Events</option>
                    </select>
                  </div>

                  {webhookFormData.webhook_event_mode === "custom" && (
                    <div className="mb-3">
                      <label className="form-label">Webhook Events</label>
                      <div className="position-relative">
                        <div
                          className="form-control scope-selector-box"
                          style={{
                            minHeight: "45px",
                            cursor: "pointer",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            alignItems: "center",
                            padding: "8px",
                          }}
                          onClick={() =>
                            setShowWebhookEventDropdown(
                              !showWebhookEventDropdown
                            )
                          }
                        >
                          {selectedWebhookEvents.length === 0 ? (
                            <span className="text-muted">
                              Select webhook events...
                            </span>
                          ) : (
                            selectedWebhookEvents.map((eventId) => {
                              const event =
                                selectedWebhookEventsData.find(
                                  (e) => e.api_webhook_event_id === eventId
                                ) ||
                                availableWebhookEvents.find(
                                  (e) => e.api_webhook_event_id === eventId
                                );
                              return event ? (
                                <span
                                  key={eventId}
                                  className="badge bg-primary d-inline-flex align-items-center gap-1 scope-chip"
                                  style={{
                                    fontSize: "13px",
                                    padding: "6px 10px",
                                  }}
                                >
                                  {event.api_webhook_event_display_name}
                                  <i
                                    className="ti ti-x"
                                    style={{
                                      fontSize: "14px",
                                      cursor: "pointer",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeWebhookEvent(eventId);
                                    }}
                                  ></i>
                                </span>
                              ) : null;
                            })
                          )}
                        </div>
                        {showWebhookEventDropdown && (
                          <>
                            <div
                              className="position-fixed"
                              style={{
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1040,
                              }}
                              onClick={() => {
                                setShowWebhookEventDropdown(false);
                                setWebhookEventSearchQuery("");
                                setWebhookEventsPage(1);
                                fetchWebhookEvents(1, "", false);
                              }}
                            />
                            <div
                              className="position-absolute w-100 bg-white border rounded shadow-lg"
                              style={{
                                zIndex: 1050,
                                top: "calc(100% + 4px)",
                              }}
                            >
                              <div
                                className="p-2 border-bottom"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Search webhook events..."
                                  value={webhookEventSearchQuery}
                                  onChange={(e) =>
                                    handleWebhookEventSearch(e.target.value)
                                  }
                                  autoFocus
                                />
                              </div>
                              <div
                                style={{
                                  maxHeight: "250px",
                                  overflowY: "auto",
                                }}
                                onScroll={(e) => {
                                  const target = e.currentTarget;
                                  const {
                                    scrollTop,
                                    scrollHeight,
                                    clientHeight,
                                  } = target;
                                  if (
                                    scrollHeight - scrollTop <=
                                    clientHeight + 5 &&
                                    webhookEventsHasMore &&
                                    !isLoadingMoreWebhookEvents
                                  ) {
                                    loadMoreWebhookEvents();
                                  }
                                }}
                              >
                                {availableWebhookEvents.length === 0 ? (
                                  <div className="p-3 text-center text-muted">
                                    {webhookEventSearchQuery
                                      ? "No events found"
                                      : "No events available"}
                                  </div>
                                ) : (
                                  <>
                                    {availableWebhookEvents.map((event) => (
                                      <div
                                        key={event.api_webhook_event_id}
                                        className={`p-3 cursor-pointer border-bottom scope-dropdown-item ${selectedWebhookEvents.includes(
                                          event.api_webhook_event_id
                                        )
                                          ? "bg-light"
                                          : ""
                                          }`}
                                        style={{ cursor: "pointer" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleWebhookEvent(
                                            event.api_webhook_event_id
                                          );
                                        }}
                                      >
                                        <div className="d-flex align-items-start justify-content-between">
                                          <div className="flex-grow-1">
                                            <div className="d-flex align-items-center gap-2">
                                              <input
                                                type="checkbox"
                                                checked={selectedWebhookEvents.includes(
                                                  event.api_webhook_event_id
                                                )}
                                                onChange={() => { }}
                                                style={{ cursor: "pointer" }}
                                              />
                                              <strong>
                                                {
                                                  event.api_webhook_event_display_name
                                                }
                                              </strong>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    {isLoadingMoreWebhookEvents && (
                                      <div className="p-3 text-center text-muted">
                                        <small>Loading more...</small>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <small className="text-muted">
                        Select the webhook events to trigger this subscription
                      </small>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={webhookFormData.description}
                      onChange={handleWebhookInputChange}
                      placeholder="Enter description"
                      rows={3}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      setShowWebhookModal(false);
                      setIsEditingWebhook(false);
                      setSelectedWebhook(null);
                      resetWebhookForm();
                      resetWebhookEventDropdownState();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={
                      isEditingWebhook
                        ? handleUpdateWebhook
                        : handleCreateWebhook
                    }
                    disabled={webhookLoading}
                  >
                    {webhookLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        {isEditingWebhook ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>
                        <i
                          className={`fa-solid ${isEditingWebhook ? "fa-save" : "fa-plus"
                            } me-1`}
                        />
                        {isEditingWebhook ? "Update Webhook" : "Create Webhook"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            className="modal-backdrop fade show"
            onClick={() => {
              setShowWebhookModal(false);
              setIsEditingWebhook(false);
              setSelectedWebhook(null);
              resetWebhookForm();
              resetWebhookEventDropdownState();
            }}
          />
        </>
      )}
    </div>
  );
};

export default ApiIntegrationSettings;
